/**
 * Painel ao lado da ficha do foco, aberto só no clique: o que é o CAR, o que
 * quer dizer a situação do imóvel escolhido e de onde vêm os dados. Fica
 * pendurado no contêiner da ficha (`.amaview-popup`, que não corta o que
 * vaza), à direita, ou à esquerda quando não cabe; acompanha a ficha quando
 * ela é arrastada. Na tela estreita (celular) abre dentro da própria ficha.
 */
import { ExternalLink, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { formatDecimal } from '../fires/format';
import { useMapInstance } from '../map/mapContext';
import { lonLatToMap } from '../map/projection';
import { intersecao, type Caixa } from '../map/popupArea';
import { painelSobreOMapa } from '../map/viewControl';
import type { ImovelCar } from './api';
import { statusCar, tipoCar } from './serie';

const LARGURA = 288; // px (w-72)
/** A linha da marca, da busca e dos botões no topo do mapa (desktop), com folga. */
const TOPO_RESERVADO_PX = 56;
/** Altura máxima do painel (px): mais que isso, rola por dentro (cobria do topo à barra do tempo). */
const ALTURA_MAX_PX = 440;

/** O que cada situação quer dizer no SICAR. */
const SIGNIFICADO_STATUS: Record<string, string> = {
  AT: 'o cadastro está válido no sistema. Não quer dizer que já foi analisado nem que o imóvel está regular.',
  PE: 'o órgão ambiental apontou pendências que o declarante precisa resolver (documentos, sobreposições, informações).',
  SU: 'o cadastro está suspenso por decisão administrativa ou judicial.',
  CA: 'o cadastro foi cancelado (por decisão administrativa, judicial ou a pedido) e não vale mais.',
};

/** O demonstrativo do imóvel no site do CAR (rota `/consultar/:codigo` do car.gov.br). */
export function urlImovelCar(cod: string): string {
  return `https://car.gov.br/#/consultar/${encodeURIComponent(cod)}`;
}

/** As situações em poucas palavras (a explicação geral, no "i"). */
const SITUACAO_CURTA: Record<string, string> = {
  AT: 'válido no sistema (não quer dizer analisado nem regular)',
  PE: 'com pendências apontadas pelo órgão ambiental',
  SU: 'suspenso por decisão administrativa ou judicial',
  CA: 'cancelado; não vale mais',
};

const FONTES: { nome: string; url: string }[] = [
  { nome: 'Consulta pública do CAR (SICAR)', url: 'https://consultapublica.car.gov.br/publico/imoveis/index' },
  { nome: 'Base pública (GeoServer do SICAR)', url: 'https://geoserver.car.gov.br/geoserver/web/' },
  { nome: 'Código Florestal, Lei nº 12.651/2012', url: 'https://www.planalto.gov.br/ccivil_03/_ato2011-2014/2012/lei/l12651.htm' },
];

function Link(props: { url: string; children: ReactNode }) {
  return (
    <a
      href={props.url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-mata-300 hover:text-mata-400 hover:underline"
    >
      {props.children}
      <ExternalLink className="size-3 shrink-0" aria-hidden />
    </a>
  );
}

function H(props: { children: ReactNode }) {
  return <h4 className="mt-2 mb-0.5 text-[11px] font-medium text-ink-200 first:mt-0">{props.children}</h4>;
}

function Conteudo(props: { imovel: ImovelCar | null; cor: string | null }) {
  const { imovel: i, cor } = props;
  return (
    <div className="text-[11.5px] leading-snug text-ink-300">
      {i && (
        <>
          <H>Imóvel selecionado</H>
          <div className="flex items-start gap-1.5">
            {cor && <span className="mt-1 inline-block h-2.5 w-3 shrink-0 rounded-[2px] border-2" style={{ borderColor: cor }} aria-hidden />}
            <span className="font-mono text-[11px] break-all text-ink-100 select-all">{i.cod}</span>
          </div>
          <a
            href={urlImovelCar(i.cod)}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1.5 inline-flex items-center gap-1.5 rounded-md border border-mata-400/40 bg-mata-400/10 px-2 py-1 font-medium text-mata-300 hover:bg-mata-400/20 hover:text-white"
            data-testid="abrir-no-sicar"
          >
            Abrir no SICAR
            <ExternalLink className="size-3.5" aria-hidden />
          </a>
          <p className="mt-1">
            <span className="text-ink-100">{statusCar(i)}</span>
            {i.status && SIGNIFICADO_STATUS[i.status] ? `: ${SIGNIFICADO_STATUS[i.status]}` : '.'}
          </p>
          {i.condicao && <p className="mt-1">Condição: {i.condicao}.</p>}
          <p className="mt-1">
            {tipoCar(i)}
            {i.areaHa != null && ` · ${formatDecimal(i.areaHa, 1)} ha`}
            {i.modulos != null && ` (${formatDecimal(i.modulos, 1)} módulos fiscais)`}.
            {i.contem ? ' O ponto do foco cai dentro dele.' : ' Toca o raio de 375 m; o ponto do foco fica fora.'}
          </p>
        </>
      )}
      {!i && (
        <>
          <H>O que é o CAR</H>
          <p>
            Registro obrigatório dos imóveis rurais (Código Florestal). O possuidor desenha o contorno e declara; o órgão
            ambiental do estado analisa depois. <strong className="text-ink-100">Não comprova propriedade</strong>, e os
            cadastros podem se sobrepor.
          </p>
          <H>Situações</H>
          <ul className="space-y-0.5">
            {Object.entries(SITUACAO_CURTA).map(([cod, texto]) => (
              <li key={cod}>
                <span className="text-ink-100">{statusCar({ status: cod } as ImovelCar)}</span>: {texto}
              </li>
            ))}
          </ul>
          <p className="mt-1 text-ink-400">Módulo fiscal: 5 a 110 ha, conforme o município; até 4 módulos é pequena propriedade.</p>
        </>
      )}
      <H>Cuidados</H>
      <p>
        A posição do foco tem incerteza de centenas de metros a quilômetros: o fogo pode estar num imóvel vizinho. Foco é
        calor detectado, não autoria. Sem nome nem CPF.
      </p>
      <H>Fonte</H>
      <p>Serviço Florestal Brasileiro (SFB), base pública do SICAR.</p>
      <ul className="mt-0.5 space-y-0.5">
        {FONTES.map((f) => (
          <li key={f.url}>
            <Link url={f.url}>{f.nome}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function useEstreito(): boolean {
  const consulta = '(max-width: 639px)';
  const [estreito, setEstreito] = useState(() => typeof matchMedia === 'function' && matchMedia(consulta).matches);
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const m = matchMedia(consulta);
    const mudou = () => setEstreito(m.matches);
    m.addEventListener('change', mudou);
    return () => m.removeEventListener('change', mudou);
  }, []);
  return estreito;
}

/**
 * `ancora`: um elemento de dentro da ficha (para achar o contêiner dela).
 * Fecha no X ou no Esc.
 */
/**
 * Lado do painel: o oposto ao foco, para não cobri-lo (a ficha já abre ao lado
 * dele); se não couber, o que couber, desde que não caia sobre o foco; senão,
 * 'dentro' (o painel abre dentro da própria ficha, como no celular).
 */
export function ladoDoPainel(
  ficha: { left: number; right: number },
  focoX: number | null,
  mapa: { left: number; right: number },
  /** Painéis sobre o mapa (a lista dos focos, a legenda) e a faixa vertical que o painel ocupará. */
  ocupadas: readonly Caixa[] = [],
  faixa: { top: number; bottom: number } = { top: 0, bottom: 0 },
): Lado {
  const direita = { left: ficha.right + 8, right: ficha.right + 8 + LARGURA };
  const esquerda = { left: ficha.left - 8 - LARGURA, right: ficha.left - 8 };
  const cobre = (x: { left: number; right: number }) => focoX !== null && focoX >= x.left - 12 && focoX <= x.right + 12;
  const sobre = (x: { left: number; right: number }) =>
    ocupadas.reduce((soma, o) => soma + intersecao({ ...x, ...faixa }, o), 0);
  const serveDireita = direita.right <= mapa.right && !cobre(direita);
  const serveEsquerda = esquerda.left >= mapa.left && !cobre(esquerda);
  const focoADireita = focoX !== null && focoX > (ficha.left + ficha.right) / 2;
  const opostoServe = focoADireita ? serveEsquerda : serveDireita;
  const opostoSobre = sobre(focoADireita ? esquerda : direita);
  const outroServe = focoADireita ? serveDireita : serveEsquerda;
  const outroSobre = sobre(focoADireita ? direita : esquerda);
  const oposto: Lado = focoADireita ? 'esquerda' : 'direita';
  const outro: Lado = focoADireita ? 'direita' : 'esquerda';
  // Primeiro, não cobrir o foco; depois, o lado que cobre menos os painéis (no empate, o oposto ao foco).
  if (opostoServe && (!outroServe || opostoSobre <= outroSobre)) return oposto;
  if (outroServe) return outro;
  return 'dentro';
}

/**
 * O pé da coluna do painel: o topo do primeiro painel do mapa abaixo dele na
 * mesma coluna (com folga), se ainda sobrar uma altura útil acima; senão, o
 * pé da parte útil.
 */
export function pisoDaColuna(x: { left: number; right: number }, util: { top: number; bottom: number }, ocupadas: readonly Caixa[]): number {
  let piso = util.bottom;
  for (const o of ocupadas) {
    const cruza = o.left < x.right && x.left < o.right;
    if (cruza && o.top > util.top + 200 && o.top - 8 < piso) piso = o.top - 8;
  }
  return piso;
}

/** Onde o painel fica na vertical: a partir do topo da ficha, mas dentro da parte útil do mapa. */
export function encaixeVertical(
  fichaTop: number,
  alturaDoPainel: number,
  util: { top: number; bottom: number },
): { top: number; maxAltura: number } {
  const maxAltura = Math.max(160, Math.min(ALTURA_MAX_PX, util.bottom - util.top));
  const altura = Math.min(alturaDoPainel, maxAltura);
  const top = Math.max(util.top, Math.min(fichaTop, util.bottom - altura));
  return { top: top - fichaTop, maxAltura };
}

type Lado = 'direita' | 'esquerda' | 'dentro';

export function PainelCar(props: {
  ancora: HTMLElement | null;
  imovel: ImovelCar | null;
  cor: string | null;
  /** O foco, para o painel abrir do outro lado dele. */
  lat: number;
  lon: number;
  onFechar: () => void;
}) {
  const { ancora, onFechar } = props;
  const estreito = useEstreito();
  const ficha = ancora?.closest<HTMLElement>('.amaview-popup') ?? null;
  const [lado, setLado] = useState<Lado>('direita');
  const [vertical, setVertical] = useState<{ top: number; maxAltura: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const map = useMapInstance((s) => s.map);
  // O lado é refeito quando o mapa anda (a ficha pode ter mudado de lado do foco).
  useLayoutEffect(() => {
    if (!ficha || estreito) return;
    const medir = () => {
      const vp = map?.getViewport().getBoundingClientRect();
      // A parte visível do mapa: sem a barra lateral que flutua sobre a borda esquerda dele.
      const mapa = vp ? { left: vp.left + painelSobreOMapa(), right: vp.right } : { left: 0, right: window.innerWidth };
      // Na vertical: abaixo da linha da busca e acima da barra do tempo.
      const barra = document.querySelector<HTMLElement>('[data-timebar]')?.getBoundingClientRect();
      const fundo = vp ? (barra && barra.height > 0 && barra.top < vp.bottom ? barra.top : vp.bottom) : window.innerHeight;
      const util = { top: (vp?.top ?? 0) + TOPO_RESERVADO_PX, bottom: fundo - 8 };
      const f = ficha.getBoundingClientRect();
      const altura = ref.current?.scrollHeight ?? 0;
      const v = encaixeVertical(f.top, altura, util);
      setVertical((a) => (a && a.top === v.top && a.maxAltura === v.maxAltura ? a : v));
      // Os painéis que o app põe sobre o mapa (irmãos dele no <main>), como no PopupHost.
      const alvo = map?.getTargetElement();
      const ocupadas: Caixa[] = [];
      for (const el of alvo?.parentElement?.children ?? []) {
        if (el === alvo || el.hasAttribute('data-moldura-previsao')) continue;
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) ocupadas.push({ left: r.left, top: r.top, right: r.right, bottom: r.bottom });
      }
      const px = map?.getPixelFromCoordinate(lonLatToMap(props.lon, props.lat));
      const faixa = { top: f.top + v.top, bottom: f.top + v.top + Math.min(altura, v.maxAltura) };
      const novoLado = ladoDoPainel(f, vp && px ? vp.left + px[0]! : null, mapa, ocupadas, faixa);
      setLado(novoLado);
      // Na coluna escolhida, o painel termina antes do que está abaixo dele (a legenda no pé do
      // mapa): o resto rola por dentro.
      if (novoLado !== 'dentro') {
        const x = novoLado === 'direita' ? { left: f.right + 8, right: f.right + 8 + LARGURA } : { left: f.left - 8 - LARGURA, right: f.left - 8 };
        const piso = pisoDaColuna(x, util, ocupadas);
        if (piso < util.bottom) {
          const v2 = encaixeVertical(f.top, altura, { top: util.top, bottom: piso });
          setVertical((a) => (a && a.top === v2.top && a.maxAltura === v2.maxAltura ? a : v2));
        }
      }
    };
    medir();
    map?.on('moveend', medir);
    // O conteúdo do painel e o tamanho da ficha mudam (imóvel escolhido, troca de aba): mede de novo.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(medir) : null;
    if (ref.current) ro?.observe(ref.current);
    ro?.observe(ficha);
    // A ficha também desliza sozinha depois de abrir (PopupHost): o painel acompanha a posição dela.
    let quadro = 0;
    let antes = '';
    const acompanhar = () => {
      const f = ficha.getBoundingClientRect();
      const agora = `${Math.round(f.left)},${Math.round(f.top)},${Math.round(f.height)}`;
      if (agora !== antes) {
        antes = agora;
        medir();
      }
      quadro = requestAnimationFrame(acompanhar);
    };
    quadro = requestAnimationFrame(acompanhar);
    return () => {
      map?.un('moveend', medir);
      ro?.disconnect();
      cancelAnimationFrame(quadro);
    };
  }, [ficha, estreito, map, props.lat, props.lon]);

  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation(); // o Esc fecha o painel, não a ficha
      onFechar();
    };
    window.addEventListener('keydown', tecla, true);
    return () => window.removeEventListener('keydown', tecla, true);
  }, [onFechar]);

  useEffect(() => {
    ref.current?.focus();
  }, [props.imovel]);

  const fechar = (
    <button
      type="button"
      aria-label="Fechar a explicação do CAR"
      className="absolute top-1.5 right-1.5 inline-flex size-6 items-center justify-center rounded-md text-ink-300 hover:bg-ink-700 hover:text-white"
      onClick={onFechar}
    >
      <X className="size-4" aria-hidden />
    </button>
  );

  if (estreito || !ficha || lado === 'dentro') {
    return (
      <div ref={ref} tabIndex={-1} className="relative mt-1.5 rounded-lg border border-ink-700 bg-ink-950/60 p-2.5 pr-8 outline-none" data-testid="painel-car" role="region" aria-label="Sobre o CAR">
        {fechar}
        <Conteudo imovel={props.imovel} cor={props.cor} />
      </div>
    );
  }
  return createPortal(
    <div
      ref={ref}
      tabIndex={-1}
      role="region"
      aria-label="Sobre o CAR"
      data-testid="painel-car"
      className={`absolute w-72 overflow-y-auto overscroll-contain rounded-xl border border-ink-600/80 bg-ink-900 p-3 pr-8 text-ink-100 shadow-2xl outline-none ${lado === 'direita' ? 'left-full ml-2' : 'right-full mr-2'}`}
      style={{ top: vertical?.top ?? 0, maxHeight: vertical?.maxAltura ?? 'calc(100dvh - 8rem)' }}
    >
      {fechar}
      <Conteudo imovel={props.imovel} cor={props.cor} />
    </div>,
    ficha,
  );
}
